import { useMutation, UseMutationOptions, useQuery } from "@tanstack/react-query";
import api from "./api-client";
import { paramsToKey } from "@/utils/helpers";
import { Pagination } from "@/types/pagination";
import { SocialMedia } from "@/types/information";
import { AboutPageResponse } from "@/types/about-us-page";
import { ContactFormData, ContactPageResponse } from "@/types/contact-us-page";
import { JoinUsPageResponse, OpeningsResponse, OpeningDetails } from "@/types/join-us-page";

const fetchContactInfo = async (params?: Record<string, string>) => {
  try {
    const response = await api.get("/information/contact/", { params });
    const contact = response.data.results[0];
    return {
      email: contact.email,
      number: contact.number,
    };
  } catch (error) {
    console.error("Failed to fetch contact info:", error);
    throw error;
  }
};

const fetchRTKInfo = async (params?: Record<string, string>) => {
  try {
    const response = await api.get("/information/about/", { params });
    return { about: response.data.results };
  } catch (error) {
    console.error("Failed to fetch about info:", error);
    throw error;
  }
};

const fetchAboutInfo = async (
  params?: Record<string, string>,
): Promise<{ about: AboutPageResponse }> => {
  try {
    const response = await api.get<AboutPageResponse>("/information/about-v2/", { params });
    return { about: response.data };
  } catch (error) {
    console.error("Failed to fetch about info:", error);
    throw error;
  }
};

const fetchContactPageInfo = async (
  params?: Record<string, string>,
): Promise<{ contact: ContactPageResponse }> => {
  try {
    const response = await api.get<ContactPageResponse>("/information/contact/", { params });
    return { contact: response.data };
  } catch (error) {
    console.error("Failed to fetch contact page info:", error);
    throw error;
  }
};

const fetchFAQsInfo = async (params?: Record<string, string>) => {
  try {
    let allResults: any[] = [];
    let page = 1;
    let response;

    do {
      response = await api.get("/information/faqs/", {
        params: { ...params, page },
      });
      allResults = allResults.concat(response.data.results);
      page += 1;
    } while (response.data.links.next);

    return { faqs: allResults };
  } catch (error) {
    console.error("Failed to fetch FAQs info:", error);
    throw error;
  }
};

const fetchSocialMediaInfo = async (params?: Record<string, string>) => {
  const queryParams = { ...params };
  const response = await api.get<Pagination<SocialMedia>>("/information/socialmedia/", {
    params: queryParams,
  });
  return response.data.results;
};

const fetchJoinUsPageInfo = async (
  params?: Record<string, string>,
): Promise<JoinUsPageResponse> => {
  try {
    const response = await api.get<JoinUsPageResponse>("/information/join/", { params });
    return response.data;
  } catch (error) {
    console.error("Failed to fetch join us page info:", error);
    throw error;
  }
};

const fetchOpenings = async (params?: Record<string, string>): Promise<OpeningsResponse> => {
  const response = await api.get<OpeningsResponse>("/openings/", { params });
  return response.data;
};

const fetchOpeningDetails = async (
  id: string,
  params?: Record<string, string>,
): Promise<OpeningDetails> => {
  const response = await api.get<OpeningDetails>(`/openings/${id}/`, { params });
  return response.data;
};

const submitContactForm = async (data: {
  name: string;
  email: string;
  phone_number: string;
  subject: string;
  message: string;
}) => {
  const response = await api.post("/information/contact/", data);
  return response.data;
};

const informationKeys = {
  contactInfo: () => ["contactInfo"],
  contactPageInfo: () => ["contactPageInfo"],
  joinUsPageInfo: () => ["joinUsPageInfo"],
  aboutInfo: () => ["aboutInfo"],
  faqsInfo: (params?: Record<string, string>) => ["faqsInfo", paramsToKey(params)],
  socialMediaInfo: () => ["socialMediaInfo"],
  openings: (params?: Record<string, string>) => ["openings", paramsToKey(params)],
  openingDetails: (id: string) => ["openingDetails", id],
};

export const useContactInfo = (params?: Record<string, string>) => {
  return useQuery({
    queryKey: [...informationKeys.contactInfo(), paramsToKey(params)],
    queryFn: () => fetchContactInfo(params),
  });
};

export const useRTKInfo = (params?: Record<string, string>) => {
  return useQuery({
    queryKey: [...informationKeys.aboutInfo(), params],
    queryFn: () => fetchRTKInfo(params),
  });
};

export const useAboutInfo = (params?: Record<string, string>) => {
  return useQuery({
    queryKey: [...informationKeys.aboutInfo(), params],
    queryFn: () => fetchAboutInfo(params),
  });
};

export const useContactPageInfo = (params?: Record<string, string>) => {
  return useQuery({
    queryKey: [...informationKeys.contactPageInfo(), params],
    queryFn: () => fetchContactPageInfo(params),
  });
};

export const useFAQsInfo = (params?: Record<string, string>) => {
  return useQuery({
    queryKey: informationKeys.faqsInfo(params),
    queryFn: () => fetchFAQsInfo(params),
  });
};

export const useSocialMediaInfo = (params?: Record<string, string>) => {
  return useQuery({
    queryKey: informationKeys.socialMediaInfo(),
    queryFn: () => fetchSocialMediaInfo(params),
  });
};

export const useJoinUsPageInfo = (params?: Record<string, string>) => {
  return useQuery({
    queryKey: [...informationKeys.joinUsPageInfo(), paramsToKey(params)],
    queryFn: () => fetchJoinUsPageInfo(params),
  });
};

export const useOpenings = (params?: Record<string, string>) => {
  return useQuery({
    queryKey: informationKeys.openings(params),
    queryFn: () => fetchOpenings(params),
  });
};

export const useOpeningDetails = (
  id: string | null,
  enabled: boolean = true,
  params?: Record<string, string>,
) => {
  return useQuery({
    queryKey: [...informationKeys.openingDetails(id || ""), paramsToKey(params)],
    queryFn: () => fetchOpeningDetails(id!, params),
    enabled: enabled && !!id,
  });
};

export interface ApplicationFormData {
  name: string;
  email: string;
  phone_number: string;
  linkedin: string;
  cv: File;
  cover_letter?: string;
}

const submitApplication = async (openingId: string, data: ApplicationFormData): Promise<any> => {
  const formData = new FormData();
  formData.append("name", data.name);
  formData.append("email", data.email);
  formData.append("phone_number", data.phone_number);
  formData.append("linkedin", data.linkedin);
  formData.append("cv", data.cv);
  if (data.cover_letter) {
    formData.append("cover_letter", data.cover_letter);
  }

  const response = await api.post(`/openings/${openingId}/applications/`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

export const useSubmitApplication = () => {
  return useMutation({
    mutationFn: ({ openingId, data }: { openingId: string; data: ApplicationFormData }) =>
      submitApplication(openingId, data),
  });
};

export const useSubmitContactForm = (
  options?: UseMutationOptions<any, Error, { data: ContactFormData }>,
) =>
  useMutation({
    mutationFn: ({ data }) => submitContactForm(data),
    ...options,
  });
